import { useCallback } from 'react';
import moment from 'moment';
import DateFilter from '@inovua/reactdatagrid-enterprise/DateFilter';
import ReactDataGrid from '@inovua/reactdatagrid-enterprise';
import { TypeFilterOperator, TypeSortInfo } from '@inovua/reactdatagrid-enterprise/types';
import DataTable, { dateRenderer, localeSort } from 'components/DataTable';
import { Text } from 'components/UI';
import { IConfigurationRequestResource } from '../../../../server/types';

const defaultColumnInfo = {
  defaultFlex: 1,
  filterDelay: 600,
};

type RenderProps = {
  data: IConfigurationRequestResource;
};

type ConfigRequestsTableProps = {
  loading: boolean;
  onLoadMore: (props: any) => Promise<{
    data: any[];
    count: number;
  }>;
  onLoadingChange: React.Dispatch<React.SetStateAction<boolean>>;
  onSortInfoChange: (sortInfo: TypeSortInfo) => void;
  total: number;
};

const columns = [
  {
    minWidth: 120,
    header: 'Date',
    name: 'createdAt',
    ...defaultColumnInfo,
    type: 'date',
    defaultFlex: 1,
    operator: 'inrange',
    dateFormat: 'YYYY/MM/DD',
    filterEditor: DateFilter,
    render: dateRenderer,
  },
  {
    minWidth: 160,
    header: 'Days Since Request',
    name: 'days',
    type: 'number',
    render: ({ data }: RenderProps) => (
      <Text as="span">{data.createdAt ? moment().endOf('day').diff(data.createdAt, 'days') : null}</Text>
    ),
    filterable: false,
    sortable: false,
    ...defaultColumnInfo,
  },
  {
    minWidth: 160,
    header: 'Design',
    name: 'productName',
    ...defaultColumnInfo,
  },
  {
    minWidth: 160,
    header: 'First Name',
    name: 'user.firstName',
    sort: localeSort,
    render: ({ data }: RenderProps) => <Text as="span">{data.user?.firstName ?? 'N/A'}</Text>,
    ...defaultColumnInfo,
  },
  {
    minWidth: 160,
    header: 'Last Name',
    name: 'user.lastName',
    sort: localeSort,
    render: ({ data }: RenderProps) => <Text as="span">{data.user?.lastName ?? 'N/A'}</Text>,
    ...defaultColumnInfo,
  },
  {
    minWidth: 200,
    header: 'Email',
    name: 'user.email',
    sort: localeSort,
    render: ({ data }: RenderProps) => <Text as="span">{data.user?.email ?? 'N/A'}</Text>,
    ...defaultColumnInfo,
  },
];

const ConfigRequestTable = ({
  loading,
  onLoadMore,
  onLoadingChange,
  onSortInfoChange,
  total,
}: ConfigRequestsTableProps) => {
  const tableActions = [
    {
      type: 'filter',
      callback: () => {},
    },
  ];

  const dataSource = useCallback(onLoadMore, []);

  return (
    <DataTable
      id="config-requests-archive-5"
      columns={columns.filter(({ name }) => name !== 'created')}
      actions={tableActions}
      enableFiltering
      loading={loading}
      total={total}
      pagination
      livePagination
      filterTypes={Object.entries(ReactDataGrid.defaultProps.filterTypes).reduce((acc, [key, val]) => {
        let value = val;
        if (key === 'date')
          value = {
            ...val,
            operators: (val.operators as TypeFilterOperator[]).filter(({ name }: TypeFilterOperator) =>
              ['inrange', 'eq'].includes(name)
            ),
          };
        if (key === 'number')
          value = {
            ...val,
            operators: (val.operators as TypeFilterOperator[]).filter(({ name }: TypeFilterOperator) =>
              ['gt', 'lt', 'gte', 'lte'].includes(name)
            ),
          };
        if (key === 'string')
          value = {
            ...val,
            operators: (val.operators as TypeFilterOperator[]).filter(
              ({ name }: TypeFilterOperator) => name === 'contains'
            ),
          };

        return { ...acc, [key]: value };
      }, {})}
      exportParams={{
        model: 'ConfigRequest',
        fields: ['createdAt', ...columns.map(({ name }) => name.replace('user.', ''))],
      }}
      tableProps={{
        idProperty: 'id',
        dataSource,
        livePagination: true,
        onSortInfoChange,
        defaultLimit: 50,
        onLoadingChange,
      }}
    />
  );
};

export default ConfigRequestTable;
